import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { useParams } from "react-router-dom";
import type {
  Branch,
  BranchData,
  Features,
  MainCategory,
  MenuItem,
  SubCategory,
} from "~/types/new_types";
import { itemIs, normalizeMenuItem } from "~/utils/menuItemHelper";
import supabase, { getAgentByBranchId } from "~/utils/supabaseClient";
import { convertAndFormatPrice } from "~/utils/currencyFormatter";
import type { Currency } from "~/types/currency";
import type { DietaryPreference } from "~/types/dietaryPreferences";
import { createUserSession } from "~/utils/supabaseClient";

// Fetch published menu by branch ID
const fetchPublishedMenu = async (branch_key: string) => {
  try {
    const { data, error } = await supabase
      .from("published_menus")
      .select("*")
      .eq("branch_key", branch_key)
      .single();
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
};

const getAgentActiveState = async (branchId: string) => {
  try {
    const { data, error } = await supabase.rpc("get_agent_active_state", {
      p_branch_id: branchId,
    });
    if (error) throw error;

    return data;
  } catch (error) {
    return false;
  }
};

export interface BranchContextType {
  // Restaurant state
  branchData: BranchData | null;
  isLoading: boolean;
  error: string | null;

  menuItems: MenuItem[]; // Updated to use menu items from published data
  selectedItem: MenuItem | null;
  setSelectedItem: React.Dispatch<React.SetStateAction<MenuItem | null>>;
  isLoadingItems: boolean;

  // Menu state
  categories: MainCategory[];
  activeCategory: string;

  theme: Branch["theme"] | null;
  branch: Branch | null;
  features: Features[] | null;
  // Actions
  loadBranch: (branchId: string) => Promise<void>;
  setActiveCategory: (categoryId: string) => void;
  clearBranch: () => void;
  refreshBranch: () => Promise<void>;

  // Utility functions
  loadAllItems: () => void;
  isAgentActive: boolean;
  filterFeaturesChildren: (features: Features[] | undefined) => Features[];

  //filters
  searchQuery: string;
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  globalSearchQuery: string;
  setGlobalSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  selectedCategoryFilter: string | null;
  setSelectedCategoryFilter: React.Dispatch<
    React.SetStateAction<string | null>
  >;
  clearFilters: () => void;
  filteredCategories: MainCategory[];
  dietaryFilters: DietaryPreference[];
  handleDietaryFilterChange: (preference: DietaryPreference) => void;
  hasActiveFilters: () => boolean;
  filteredItems: MenuItem[];
  filterMenuItemsByName: (text: string) => void;
  getItemsByCategoryId: (categoryId: string) => MenuItem[];
  filterItemsByCategoryAndSearch: (
    categoryId: string | null,
    searchText: string
  ) => MenuItem[];
  // Currency state
  currencies: Currency[] | null;
  isLoadingCurrencies: boolean;
  selectedCurrency: Currency | null;
  setSelectedCurrency: React.Dispatch<React.SetStateAction<Currency | null>>;
  // Currency helper function
  getConvertedPrice: (price: number) => string;

  // Dietary preferences
  dietaryPreferences: DietaryPreference[];
}

const BranchContext = createContext<BranchContextType | undefined>(undefined);

interface BranchProviderProps {
  children: ReactNode;
}

export const BranchProvider: React.FC<BranchProviderProps> = ({ children }) => {
  const [branchData, setBranchData] = useState<BranchData | null>(null);
  const [features, setFeatures] = useState<Features[] | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isAgentActive, setIsAgentActive] = useState<boolean>(false);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<MenuItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null);
  const [isLoadingItems, setIsLoadingItems] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>("");
  const [categories, setCategories] = useState<MainCategory[]>([]);
  const [currencies, setCurrencies] = useState<Currency[] | null>(null);
  const [isLoadingCurrencies, setIsLoadingCurrencies] =
    useState<boolean>(false);
  const [selectedCurrency, setSelectedCurrency] = useState<Currency | null>(
    null
  );
  const { branch_key } = useParams<{ branch_key: string }>();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [globalSearchQuery, setGlobalSearchQuery] = useState<string>("");
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<
    string | null
  >(null);
  const [filteredCategories, setFilteredCategories] = useState<MainCategory[]>(
    []
  );
  const [dietaryFilters, setDietaryFilters] = useState<DietaryPreference[]>([]);
  const [dietaryPreferences, setDietaryPreferences] = useState<
    DietaryPreference[]
  >([]);

  const getItemsByCategoryId = (categoryId: string): MenuItem[] => {
    // Find all items that belong to this category or its subcategories
    const category = categories.find((cat) => cat.id === categoryId);
    if (!category) return [];

    // Get direct items from the main category
    const directItems = menuItems.filter(
      (item) => item.category_id === categoryId && item.is_published
    );

    // Get items from subcategories
    const subCategoryIds = category.subCategories.map((subCat) => subCat.id);
    const subCategoryItems = menuItems.filter((item) =>
      subCategoryIds.includes(item.category_id)
    );

    return [...directItems, ...subCategoryItems];
  };

  const filterItemsByCategoryAndSearch = (
    categoryId: string | null,
    searchText: string
  ): MenuItem[] => {
    const query = searchText.toLowerCase().trim();
    const anyDietaryActive = dietaryFilters.length > 0;
    const anyActive = Boolean(categoryId) || Boolean(query) || anyDietaryActive;

    if (!anyActive) return [];

    // Start scope: by category or all published items
    let itemsToFilter: MenuItem[] = categoryId
      ? getItemsByCategoryId(categoryId)
      : menuItems.filter((i) => i.is_published);

    // Dietary filters - filter based on item.dietary_preferences array
    if (anyDietaryActive) {
      itemsToFilter = itemsToFilter.filter((item) => {
        const itemDietaryIds = (item.dietary_preferences || []).map(
          (dp) => dp.id
        );
        // Item must have ALL selected dietary preferences
        return dietaryFilters.every((selectedPref) =>
          itemDietaryIds.includes(selectedPref.id)
        );
      });
    }

    // Search filter
    if (query) {
      itemsToFilter = itemsToFilter.filter(
        (item) =>
          item.name.toLowerCase().includes(query) ||
          item.ar?.name?.toLowerCase().includes(query) ||
          item.description?.toLowerCase().includes(query) ||
          item.ar?.description?.toLowerCase().includes(query)
      );
    }
    return itemsToFilter;
  };
  useEffect(() => {
    const filtered = filterItemsByCategoryAndSearch(
      selectedCategoryFilter,
      globalSearchQuery
    );
    setFilteredItems(filtered);
  }, [selectedCategoryFilter, globalSearchQuery, dietaryFilters, menuItems]);
  const filterMenuItemsByName = (text: string) => {
    setGlobalSearchQuery(text);
    const filtered = menuItems.filter((item) =>
      item.name.toLowerCase().includes(text.toLowerCase())
    );
    setFilteredItems(filtered);
  };
  const handleDietaryFilterChange = (preference: DietaryPreference) => {
    setDietaryFilters((prev) => {
      const isAlreadySelected = prev.some((p) => p.id === preference.id);
      if (isAlreadySelected) {
        // Remove if already selected
        return prev.filter((p) => p.id !== preference.id);
      } else {
        // Add if not selected
        return [...prev, preference];
      }
    });
  };
  useEffect(() => {
    if (!branchData) return;

    const filterItems = () => {
      const query = searchQuery.toLowerCase().trim();

      const filtered: MainCategory[] = categories
        .map((cat1) => {
          const directItems = (cat1.items ?? []).filter((item) => {
            const matchesSearch =
              !query ||
              item.name.toLowerCase().includes(query) ||
              item.ar?.name?.toLowerCase().includes(query);

            const itemDietaryIds = (item.dietary_preferences || []).map(
              (dp) => dp.id
            );
            const matchesDietary =
              dietaryFilters.length === 0 ||
              dietaryFilters.every((selectedPref) =>
                itemDietaryIds.includes(selectedPref.id)
              );

            return matchesSearch && matchesDietary;
          });

          const subCats = cat1.subCategories
            .map((subCat) => {
              const items = subCat.items.filter((item) => {
                const matchesSearch =
                  !query ||
                  item.name.toLowerCase().includes(query) ||
                  item.ar?.name?.toLowerCase().includes(query);

                const itemDietaryIds = (item.dietary_preferences || []).map(
                  (dp) => dp.id
                );
                const matchesDietary =
                  dietaryFilters.length === 0 ||
                  dietaryFilters.every((selectedPref) =>
                    itemDietaryIds.includes(selectedPref.id)
                  );

                return matchesSearch && matchesDietary;
              });
              return { ...subCat, itemsCount: items.length, items };
            })
            .filter((subCat) => subCat.itemsCount > 0);

          const itemsCount =
            directItems.length +
            subCats.reduce((count, cat) => count + cat.itemsCount, 0);

          return {
            ...cat1,
            itemsCount,
            subCategories: subCats,
            items: directItems,
          };
        })
        .filter((cat) => cat.itemsCount > 0);

      setFilteredCategories(filtered);
    };

    filterItems();
  }, [searchQuery, dietaryFilters, activeCategory, branchData, categories]);
  const clearFilters = () => {
    setSearchQuery("");
    setSelectedCategoryFilter(null);
    setDietaryFilters([]);
  };
  const hasActiveFilters = () => {
    return (
      searchQuery.trim() !== "" ||
      dietaryFilters.length > 0 ||
      selectedCategoryFilter !== null
    );
  };

  const loadBranch = async (branch_key: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await fetchPublishedMenu(branch_key);

      if (!result.success || !result.data) {
        throw new Error(result.error || "Failed to fetch published menu");
      }

      const publishedMenu = result.data;
      const menuObject = publishedMenu.menu_object;

      if (!menuObject || typeof menuObject !== "object") {
        throw new Error("Invalid menu object structure");
      }

      const menuData = menuObject as any;
      const actualBranchId = menuData.branch?.id;

      if (!actualBranchId) {
        throw new Error("Branch ID not found in menu data");
      }

      const rawItems = Array.isArray(menuData.items)
        ? (menuData.items as MenuItem[])
        : [];
      const normalizedItems = rawItems.map((item) => normalizeMenuItem(item));

      // Transform the data to match your existing BranchData structure
      const transformedBranchData: BranchData = {
        branch: menuData.branch,
        categories: menuData.categories || [],
        items: normalizedItems,
        summary: menuData.summary,
        delivery_zones: menuData.delivery_zones || [],
        // reviews: [],
      };

      setBranchData(transformedBranchData);
      setFeatures(menuData.features || null);
      setMenuItems(normalizedItems);

      loadCategories(transformedBranchData);

      getAgentActiveState(actualBranchId).then((state: boolean) =>
        setIsAgentActive(state)
      );

      // Load currencies from published menu
      setIsLoadingCurrencies(true);
      if (menuData.currencies && Array.isArray(menuData.currencies)) {
        const currencies = menuData.currencies as Currency[];
        setCurrencies(currencies);
        // Set default selected currency to the base currency
        const baseCurrency = currencies.find((c) => c.is_base);
        if (baseCurrency) {
          setSelectedCurrency(baseCurrency);
        } else if (currencies.length > 0) {
          setSelectedCurrency(currencies[0]);
        }
      } else {
        setCurrencies(null);
        setSelectedCurrency(null);
      }
      setIsLoadingCurrencies(false);

      // Load dietary preferences from published menu
      if (menuData.dietary_preferences && Array.isArray(menuData.dietary_preferences)) {
        setDietaryPreferences(menuData.dietary_preferences as DietaryPreference[]);
      } else {
        setDietaryPreferences([]);
      }

      // Create user session for the branch
      try {
        await createUserSession(actualBranchId);
        // console.log('User session created successfully');
      } catch (sessionErr) {
        console.error('Error creating user session:', sessionErr);
        // Don't throw - session creation failure shouldn't break the app
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to load published menu";
      setError(errorMessage);
      console.error("Error loading published menu:", err);
    } finally {
      setIsLoading(false);
    }
  };
  const loadCategories = (branchData: BranchData) => {
    if (!branchData || !branchData.categories) return;

    let mainCategories: MainCategory[] = branchData.categories
      .filter((cat) => !cat.parent_id)
      .map(
        (cat) =>
          ({
            id: cat.id,
            created_at: cat.created_at,
            description: cat.description,
            name: cat.name,
            image_url: cat.image_url,
            updated_at: cat.updated_at,
            is_published: cat.is_published,
            ar: cat.ar,
            itemsCount: 0,
            subCategories: [],
            items: [],
          } as MainCategory)
      );

    let subCategories: SubCategory[] = branchData.categories
      .filter((cat) => cat.parent_id)
      .map(
        (cat) =>
          ({
            id: cat.id,
            created_at: cat.created_at,
            description: cat.description,
            name: cat.name,
            image_url: cat.image_url,
            updated_at: cat.updated_at,
            is_published: cat.is_published,
            ar: cat.ar,
            itemsCount: 0,
            parentCategory: mainCategories.find(
              (cat2) => cat2.id === cat.parent_id
            ),
          } as SubCategory)
      );

    subCategories = subCategories.map((cat) => {
      const items = branchData.items.filter(
        (item) => item.category_id === cat.id && item.is_published
      );
      return { ...cat, itemsCount: items.length, items: items };
    });

    mainCategories = mainCategories.map((cat) => {
      const subCats = subCategories.filter(
        (subCat) => subCat.parentCategory?.id === cat.id
      );
      const directItems = branchData.items.filter(
        (item) => item.category_id === cat.id && item.is_published
      );
      // Only count published subcategories
      const publishedSubCats = subCats.filter((subCat) => subCat.is_published);
      const itemsCount =
        publishedSubCats.reduce((count, cat) => count + cat.itemsCount, 0) +
        directItems.length;
      return {
        ...cat,
        itemsCount: itemsCount,
        subCategories: subCats,
        items: directItems,
      };
    });

    setCategories(mainCategories);
  };
  useEffect(() => {
    if (branch_key) {
      loadBranch(branch_key);
    }
  }, [branch_key]);
  useEffect(() => {
    // Set first category as active if none is set
    if (categories && categories.length > 0 && !activeCategory) {
      setActiveCategory(categories[0].id);
    }
  }, [categories]);
  const refreshBranch = async () => {
    if (branchData?.branch?.id) {
      await loadBranch(branchData.branch.id);
    }
  };
  const filterFeaturesChildren = (features: Features[] | undefined) => {
    if (!features) return [];
    return features.filter((feature) =>
      feature.children?.some((child) => child.enabled)
    );
  };
  const clearBranch = () => {
    setBranchData(null);
    setCategories([]);
    setActiveCategory("");
    setError(null);
    setMenuItems([]);
  };
  // Set active category
  const handleSetActiveCategory = (categoryId: string) => {
    setActiveCategory(categoryId);
  };
  // Load all items (now just returns the already loaded items)
  const loadAllItems = () => {
    setIsLoadingItems(true);
    try {
      // Items are already loaded with the branch data
      // Just set loading to false
      setIsLoadingItems(false);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to load Items";
      setError(errorMessage);
      console.error("Error loading items:", err);
      setMenuItems([]);
      setIsLoadingItems(false);
    }
  };
  // Centralized currency conversion helper - converts price and shows correct currency symbol with thousand separators
  const getConvertedPrice = (price: number): string => {
    if (!selectedCurrency || selectedCurrency.exchange_rate === null) {
      // Fallback to USD if no currency selected
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(price);
    }
    const convertedPrice = price * selectedCurrency.exchange_rate;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: selectedCurrency.iso,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(convertedPrice);
  };
  const contextValue: BranchContextType = {
    // State
    branchData,
    isLoading,
    error,
    categories,
    activeCategory,
    menuItems,
    isLoadingItems,
    selectedItem,
    setSelectedItem,
    globalSearchQuery,
    setGlobalSearchQuery,
    selectedCategoryFilter,
    setSelectedCategoryFilter,

    theme: branchData?.branch?.theme ?? null,
    branch: branchData?.branch ?? null,
    features: features,
    searchQuery,
    setSearchQuery,
    clearFilters,
    dietaryFilters,
    handleDietaryFilterChange,
    filteredCategories,
    hasActiveFilters,

    isAgentActive,

    // Currencies
    currencies,
    isLoadingCurrencies,
    selectedCurrency,
    setSelectedCurrency,
    // Currency helper
    getConvertedPrice,
    // Actions
    loadBranch,
    setActiveCategory: handleSetActiveCategory,
    clearBranch,
    refreshBranch,
    loadAllItems,
    filteredItems,
    filterMenuItemsByName,
    filterFeaturesChildren,
    getItemsByCategoryId,
    filterItemsByCategoryAndSearch,

    // Dietary preferences
    dietaryPreferences,
  };

  return (
    <BranchContext.Provider value={contextValue}>
      {children}
    </BranchContext.Provider>
  );
};

// Custom hook to use branch context
export const useBranch = (): BranchContextType => {
  const context = useContext(BranchContext);
  if (context === undefined) {
    throw new Error("useBranch must be used within a BranchProvider");
  }
  return context;
};
